import React, { useEffect, useState } from 'react';
import { visitsAPI, clientsAPI, caregiverProfilesAPI } from '../services/api';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { toast } from 'sonner';

/**
 * VisitEditDialog — reusable dialog for editing/assigning a single visit.
 * Shows caregivers in the dropdown with proximity (miles from client address).
 */
const VisitEditDialog = ({ open, onOpenChange, visit, onSaved }) => {
  const [form, setForm] = useState({
    scheduled_date: '', scheduled_time: '',
    duration_minutes: 60, caregiver_id: '', notes: ''
  });
  const [caregivers, setCaregivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open || !visit) return;
    setForm({
      scheduled_date: visit.scheduled_date || '',
      scheduled_time: visit.scheduled_time || '',
      duration_minutes: visit.duration_minutes || 60,
      caregiver_id: visit.caregiver_id || '',
      notes: visit.notes || ''
    });
    loadCaregivers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, visit?.id]);

  const loadCaregivers = async () => {
    if (!visit?.client_id) return;
    setLoading(true);
    try {
      // Try distance-aware endpoint first; fall back to plain list on error
      try {
        const res = await clientsAPI.getCaregiverDistances(visit.client_id);
        setCaregivers(res.data || []);
      } catch {
        const res = await caregiverProfilesAPI.getAll();
        const active = (res.data || [])
          .filter(p => (p.status || 'active') !== 'inactive'
                    && (p.employment_status || 'active') === 'active')
          .map(p => ({
            id: p.user_id && p.user_id !== 'none' ? p.user_id : `__nologin__:${p.id}`,
            name: p.name,
            distance_miles: null,
            needs_login: !p.user_id || p.user_id === 'none',
            profile_id: p.id,
          }))
          .sort((a, b) => a.name.localeCompare(b.name));
        setCaregivers(active);
      }
    } finally { setLoading(false); }
  };

  const handleSave = async () => {
    if (!visit) return;
    if (!form.scheduled_date || !form.scheduled_time) {
      toast.error('Date and time are required');
      return;
    }
    setSaving(true);
    try {
      await visitsAPI.update(visit.id, {
        client_id: visit.client_id,
        caregiver_id: form.caregiver_id || '',
        scheduled_date: form.scheduled_date,
        scheduled_time: form.scheduled_time,
        duration_minutes: parseInt(form.duration_minutes) || 60,
        notes: form.notes || null
      });
      toast.success('Visit updated');
      if (onSaved) onSaved();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update visit');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Edit Visit</DialogTitle>
          <DialogDescription>Change caregiver, date, time, or notes for this single visit.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date *</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setForm({ ...form, scheduled_date: e.target.value })} className="rounded-xl" data-testid="dash-visit-date" />
            </div>
            <div className="space-y-2">
              <Label>Time *</Label>
              <Input type="time" value={form.scheduled_time} onChange={(e) => setForm({ ...form, scheduled_time: e.target.value })} className="rounded-xl" data-testid="dash-visit-time" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Duration</Label>
            <Select value={String(form.duration_minutes)} onValueChange={(v) => setForm({ ...form, duration_minutes: parseInt(v) })}>
              <SelectTrigger className="rounded-xl" data-testid="dash-visit-duration"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="30">30 min</SelectItem>
                <SelectItem value="60">1 hour</SelectItem>
                <SelectItem value="90">1.5 hours</SelectItem>
                <SelectItem value="120">2 hours</SelectItem>
                <SelectItem value="180">3 hours</SelectItem>
                <SelectItem value="240">4 hours</SelectItem>
                <SelectItem value="300">5 hours</SelectItem>
                <SelectItem value="360">6 hours</SelectItem>
                <SelectItem value="420">7 hours</SelectItem>
                <SelectItem value="480">8 hours</SelectItem>
                <SelectItem value="540">9 hours</SelectItem>
                <SelectItem value="600">10 hours</SelectItem>
                <SelectItem value="660">11 hours</SelectItem>
                <SelectItem value="720">12 hours</SelectItem>
                <SelectItem value="780">13 hours</SelectItem>
                <SelectItem value="840">14 hours</SelectItem>
                <SelectItem value="900">15 hours</SelectItem>
                <SelectItem value="960">16 hours</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Caregiver</Label>
              {loading && <span className="text-[10px] text-muted-foreground">calculating distance…</span>}
            </div>
            <Select value={form.caregiver_id || 'none'} onValueChange={(v) => setForm({ ...form, caregiver_id: v === 'none' ? '' : v })}>
              <SelectTrigger className="rounded-xl" data-testid="dash-visit-caregiver">
                <SelectValue placeholder="Unassigned" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Unassigned</SelectItem>
                {caregivers.map(cg => (
                  <SelectItem key={cg.id} value={cg.id} disabled={cg.needs_login}>
                    <span className="flex items-center justify-between gap-3 w-full">
                      <span>
                        {cg.name}
                        {cg.is_supervisor && <span className="ml-2 text-[10px] uppercase tracking-wider text-primary font-semibold">Supervisor</span>}
                      </span>
                      <span className="text-[11px] ml-auto">
                        {cg.needs_login ? (
                          <span className="text-amber-600 font-medium">(needs login)</span>
                        ) : (
                          <span className="text-muted-foreground">{cg.distance_miles != null ? `${cg.distance_miles} mi away` : 'distance unknown'}</span>
                        )}
                      </span>
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-muted-foreground">Caregivers are sorted closest first based on their home address.</p>
          </div>
          <div className="space-y-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Visit notes..." className="rounded-xl" data-testid="dash-visit-notes" />
          </div>
          <div className="flex gap-3 pt-4">
            <Button variant="outline" className="flex-1 rounded-xl" onClick={() => onOpenChange(false)} disabled={saving}>Cancel</Button>
            <Button className="flex-1 rounded-xl" onClick={handleSave} disabled={saving} data-testid="dash-save-visit">
              {saving ? 'Saving...' : 'Save Visit'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VisitEditDialog;
